import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../constants/routes'

import { useFormik, Form, FormikProvider } from 'formik'
import * as Yup from 'yup'

import {
  useGetClientQuery,
  useAddClientMutation,
  useUpdateClientMutation,
} from '../slice'
import { useGetUsersQuery } from '../../users/slice'

import MuiBox from '@mui/material/Box'
import MuiTypography from '@mui/material/Typography'
import MuiDivider from '@mui/material/Divider'
import MuiButton from '@mui/material/Button'
import MuiLoadingButton from '@mui/lab/LoadingButton'

import FormikControl from '../../../components/formik-control'
import PageTitle from '../../../components/page-title'
import LoadingScreen from '../../../components/loading-screen'
import Producers from './producers'
import Campaigns from './campaigns'

const ClientDetails = () => {
  const { clientId } = useParams()
  const navigate = useNavigate()

  const { data: client, isLoading: isClientLoading } = useGetClientQuery(
    clientId,
    { skip: clientId === 'new' }
  )
  const { data: users, isLoading: isUsersLoading } = useGetUsersQuery()

  const [addClient] = useAddClientMutation()
  const [updateClient] = useUpdateClientMutation()
  const [ownerOptions, setOwnerOptions] = useState([])

  useEffect(() => {
    if (users) {
      const mappedUsers = users.map((user) => ({
        value: user.sub,
        key: `${user.givenName} ${user.familyName}`,
      }))
      const blankOption = [{ value: '', key: '< No Owner >' }]
      setOwnerOptions(blankOption.concat(mappedUsers))
    }
  }, [users])

  useEffect(() => {
    if (client) {
      const values = {
        ...formik.values,
        id: client.id,
        name: client.name || '',
        description: client.description || '',
        owner: client.owner || '',
        about: client.about || '',
        website: client.website || '',
        isActive: client.isActive ?? true,
      }
      formik.setValues(values)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])

  const initialValues = {
    id: undefined,
    name: '',
    description: '',
    owner: '',
    about: '',
    website: '',
    isActive: true,
  }

  const validationSchema = Yup.object({
    name: Yup.string().required('Required'),
  })

  const onSubmit = async (values) => {
    if (clientId !== 'new') {
      await updateClient(values)
      navigate(ROUTES.CLIENTS, { replace: true })
    } else {
      const { data: result } = await addClient(values)
      navigate(`${ROUTES.CLIENTS}/${result.data.id}`, { replace: true })
    }
  }

  const handleSaveClick = () => {
    formik.handleSubmit()
  }

  const handleBackClick = () => {
    navigate(ROUTES.CLIENTS)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  const action = (
    <>
      <MuiButton
        variant="outlined"
        color="error"
        onClick={handleBackClick}
        sx={{ ml: 1 }}
      >
        Back
      </MuiButton>
      <MuiLoadingButton
        variant="outlined"
        onClick={handleSaveClick}
        sx={{ ml: 1 }}
        loading={formik.isSubmitting}
      >
        Save
      </MuiLoadingButton>
    </>
  )

  if (isClientLoading || isUsersLoading || ownerOptions.length === 0)
    return <LoadingScreen />
  return (
    <FormikProvider value={formik}>
      <PageTitle
        title={`Clients :: ${clientId === 'new' ? 'New Client' : client?.name}`}
        action={action}
      />
      <Form>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column' }}>
          <MuiTypography variant="h6">IN BRIEF</MuiTypography>
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            <MuiTypography sx={{ width: 150 }}>Name</MuiTypography>
            <FormikControl
              control="input"
              label="Name"
              name="name"
              sx={{ width: 400, mt: 1 }}
            />
          </MuiBox>
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            <MuiTypography sx={{ width: 150 }}>Description</MuiTypography>
            <FormikControl
              control="input"
              label="Description"
              name="description"
              sx={{ mt: 1, flexGrow: 1 }}
            />
          </MuiBox>
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            <MuiTypography sx={{ width: 150 }}>Owner</MuiTypography>
            <FormikControl
              control="select"
              label="Owner"
              name="owner"
              options={ownerOptions}
              sx={{ width: 400 }}
            />
          </MuiBox>
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            <MuiTypography sx={{ width: 150 }}>Website</MuiTypography>
            <FormikControl
              control="input"
              label="Website"
              name="website"
              sx={{ width: 400 }}
            />
          </MuiBox>
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            <MuiTypography sx={{ width: 150 }}>&nbsp;</MuiTypography>
            <FormikControl control="checkbox" label="Active" name="isActive" />
          </MuiBox>
          <MuiDivider sx={{ my: 2 }} />
          <MuiTypography variant="h6">ABOUT</MuiTypography>
          <MuiBox sx={{ display: 'flex' }}>
            <FormikControl
              control="textarea"
              label="About"
              rows={4}
              name="about"
              sx={{ flexGrow: 1 }}
            />
          </MuiBox>
        </MuiBox>
        <MuiDivider sx={{ my: 2 }} />
        <Producers client={client} />
        <MuiDivider sx={{ my: 2 }} />
        <Campaigns client={client} />
      </Form>
    </FormikProvider>
  )
}

export default ClientDetails
